import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, LayoutDashboard } from "lucide-react";
import DestinationCard from "@/components/DestinationCard";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [favs, setFavs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    api.get("/favorites").then(({ data }) => { setFavs(data); setFavIds(new Set(data.map((d) => d.id))); });
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-sm text-muted-foreground">Loading...</div>;
  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <h1 className="heading-serif text-3xl mb-4">Please sign in</h1>
      <Button asChild className="rounded-full bg-accent text-accent-foreground"><Link to="/login">Sign in</Link></Button>
    </div>
  );

  const toggleFav = async (d) => {
    await api.delete(`/favorites/${d.id}`);
    setFavs(favs.filter((x) => x.id !== d.id));
    const n = new Set(favIds); n.delete(d.id); setFavIds(n);
    toast.success("Removed from favorites");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-14">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent mb-2">Your account</div>
          <h1 className="heading-serif text-5xl">Hello, {user.name || user.email}</h1>
        </div>
        {user.is_admin && (
          <Button asChild variant="outline" className="rounded-full" data-testid="admin-panel-link">
            <Link to="/admin"><LayoutDashboard className="mr-2 h-4 w-4" /> Open Admin C-Panel</Link>
          </Button>
        )}
      </div>

      <section className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-serif text-3xl flex items-center gap-2"><Heart className="h-5 w-5 text-accent" /> Your favorites</h2>
          <Link to="/favorites" className="text-sm text-muted-foreground underline-anim flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {favs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No favorites yet. Tap the heart on any destination to save it.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favs.slice(0, 6).map((d) => <DestinationCard key={d.id} d={d} isFav={true} onToggleFav={toggleFav} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="heading-serif text-3xl mb-6">Your bookings</h2>
        {bookings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No confirmed bookings yet.</p>
        ) : (
          <div className="border border-border rounded-xl overflow-hidden" data-testid="bookings-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr>
                <th className="p-3">Destination</th><th className="p-3">Payer</th><th className="p-3">Phone</th><th className="p-3">Travelers</th><th className="p-3">Amount</th><th className="p-3">Status</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.session_id} className="border-t border-border">
                    <td className="p-3">{b.destination_name}</td>
                    <td className="p-3">{b.customer_name || user.name || "—"}</td>
                    <td className="p-3">{b.customer_phone || "—"}</td>
                    <td className="p-3">{b.travelers}</td>
                    <td className="p-3">₹{Number(b.amount).toLocaleString("en-IN")}</td>
                    <td className="p-3 text-accent">{b.payment_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
